interface DashboardBatchResult {
  rows: Record<string, unknown>[]
}

interface RemainderAccumulator {
  count: number
  row: Record<string, unknown>
  weightedValue: number
}

const readStringArray = (value: unknown): string[] | null =>
  Array.isArray(value) && value.every((item) => typeof item === "string")
    ? value
    : null

const getResultTopValues = (result: DashboardBatchResult): string[] | null => {
  for (const row of result.rows) {
    const topValues = readStringArray(row.metadata_top_values)
    if (topValues) {
      return topValues
    }
  }

  return null
}

const normalizeResultRows = ({
  result,
  sharedTopValues,
}: {
  result: DashboardBatchResult
  sharedTopValues: string[]
}): Record<string, unknown>[] => {
  const sharedTopValueSet = new Set(sharedTopValues)
  const rows: Record<string, unknown>[] = []
  const remainders = new Map<string, RemainderAccumulator>()

  for (const row of result.rows) {
    if (
      row.metadata_group_kind === "value" &&
      typeof row.metadata_value === "string" &&
      sharedTopValueSet.has(row.metadata_value)
    ) {
      rows.push({ ...row, metadata_top_values: sharedTopValues })
      continue
    }

    const metricId = typeof row.metric_id === "string" ? row.metric_id : ""
    const count = typeof row.count === "number" ? row.count : Number(row.count)
    const value = typeof row.value === "number" ? row.value : Number(row.value)
    const resolvedCount = Number.isFinite(count) ? count : 0
    const resolvedValue = Number.isFinite(value) ? value : 0
    const accumulator = remainders.get(metricId) ?? {
      count: 0,
      row: {
        ...row,
        metadata_group_kind: "remainder",
        metadata_top_values: sharedTopValues,
        metadata_value: "Other",
      },
      weightedValue: 0,
    }

    accumulator.count += resolvedCount
    accumulator.weightedValue += resolvedValue * resolvedCount
    remainders.set(metricId, accumulator)
  }

  for (const accumulator of remainders.values()) {
    rows.push({
      ...accumulator.row,
      count: accumulator.count,
      value:
        accumulator.count > 0
          ? accumulator.weightedValue / accumulator.count
          : 0,
    })
  }

  return rows
}

export const reconcileTableMetadataCohorts = (
  results: DashboardBatchResult[],
): Record<string, unknown>[] => {
  const topValueSets = results
    .map(getResultTopValues)
    .filter((topValues): topValues is string[] => topValues !== null)

  if (topValueSets.length < 2) {
    return results.flatMap((result) => result.rows)
  }

  const sharedTopValues = topValueSets[0].filter((value) =>
    topValueSets.every((topValues) => topValues.includes(value)),
  )

  return results.flatMap((result) =>
    normalizeResultRows({ result, sharedTopValues }),
  )
}
